"use client";
import { Copy, Check, AlignCenter } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import React, { useCallback, useRef, useEffect, useState } from "react";
import { config } from "@fortawesome/fontawesome-svg-core";
import "@fortawesome/fontawesome-svg-core/styles.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLocationDot, faPhone, faEnvelope } from "@fortawesome/free-solid-svg-icons";
import { faClock } from "@fortawesome/free-regular-svg-icons";
import { faWhatsapp, faFacebookF, faInstagram, faTiktok } from "@fortawesome/free-brands-svg-icons";

config.autoAddCss = false;

function useInViewOnce(ref) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref]);
  return inView;
}

export function MainFooter({ footerProps, className, walink }) {
  const [copied, setCopied] = React.useState(false);
  const gridRef = useRef(null);
  const bottomRef = useRef(null);
  const gridInView = useInViewOnce(gridRef);
  const bottomInView = useInViewOnce(bottomRef);

  const getEmail = useCallback(() => {
    return footerProps.emailParts ? footerProps.emailParts.join("") : "";
  }, [footerProps.emailParts]);

  const handleCopyEmail = useCallback(() => {
    const email = getEmail();
    if (email) {
      navigator.clipboard.writeText(email);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [getEmail]);

  // Icon mapping for social media
  const socialIcons = {
    facebook: faFacebookF,
    instagram: faInstagram,
    tiktok: faTiktok,
  };

  return (
    <footer
      id={footerProps.id}
      className={`relative py-16 mt-5 overflow-hidden border-t-4 scroll-mt-20 border-primary`}
    >
      {/* Botón flotante de WhatsApp */}
      {footerProps.whatsapp && (
        <div className="fixed bottom-6 right-6 z-50">
          <a
            href={walink}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center w-14 h-14 bg-green-500 hover:bg-green-600 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group hover:scale-110 active:scale-90 animate-[bounce-in_0.5s_ease-out_1s_both]"
          >
            <FontAwesomeIcon icon={faWhatsapp} className="text-2xl text-white group-hover:animate-pulse" />

            {/* Tooltip */}
            <div className="absolute right-16 top-1/2 transform -translate-y-1/2 bg-gray-800 text-white px-3 py-2 rounded-lg text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">
              ¡Contáctanos por WhatsApp!
              <div className="absolute top-1/2 right-0 transform translate-x-1 -translate-y-1/2 w-0 h-0 border-l-4 border-l-gray-800 border-t-4 border-t-transparent border-b-4 border-b-transparent"></div>
            </div>
          </a>
        </div>
      )}

      <div className="container mx-auto px-4 relative z-10 max-w-7xl">
        {/* Main Content Grid */}
        <div
          ref={gridRef}
          className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12 mb-12 transition-opacity duration-300 ${gridInView ? "opacity-100" : "opacity-0"}`}
        >
          {/* Company Info Section */}
          <div className={`space-y-4 transition-all duration-400 ease-out ${gridInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`} style={{ transitionDelay: "0ms" }}>
            <div className="relative bg-white rounded-2xl p-4 shadow-lg hover:shadow-xl transition-shadow duration-300 border border-slate-200/50">
              <a
                className="flex justify-center mb-4"
                href={footerProps.href}
              >
                <Image
                  src={footerProps.image.name}
                  width={footerProps.image.width}
                  height={footerProps.image.height}
                  alt={footerProps.image.alt}
                  priority
                  className="hover:scale-105 transition-transform duration-300"
                />
              </a>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <FontAwesomeIcon
                    icon={faLocationDot}
                    className="text-primary mt-1 flex-shrink-0 text-xl md:text-base"
                  />
                  <p
                    className="text-slate-600 text-lg md:text-sm font-medium leading-relaxed"
                  >
                    <strong>{footerProps.companyAddress.city}</strong> {footerProps.companyAddress.rest}
                  </p>
                </div>

                <div className="flex items-start space-x-3">
                  <FontAwesomeIcon
                    icon={faLocationDot}
                    className="text-primary mt-1 flex-shrink-0 text-xl md:text-base"
                  />
                  <p
                    className="text-slate-600 text-lg md:text-sm font-medium leading-relaxed"
                  >
                    <strong>{footerProps.companyAddress2.city}</strong> {footerProps.companyAddress2.rest}
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <FontAwesomeIcon
                    icon={faClock}
                    className="text-primary flex-shrink-0 text-xl md:text-base mt-1"
                  />
                  <div
                    className="text-slate-600 text-lg md:text-sm font-medium"
                  >
                    {footerProps.companyHours.map((hour, index) => (
                      <span key={index} className="block">
                        {hour}
                      </span>
                    ))}

                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Info Section */}
          <div className={`space-y-4 transition-all duration-400 ease-out ${gridInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`} style={{ transitionDelay: "80ms" }}>
            <h3
              className="text-2xl md:text-xl font-bold text-title mb-4"
            >
              Contacto
            </h3>
            <div className="space-y-3">
              <div className="flex items-center space-x-3 group">
                <div
                  className="w-14 md:w-10 h-14 md:h-10 bg-gradient-to-br from-primary to-red-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 flex-shrink-0"

                >
                  <FontAwesomeIcon icon={faPhone} className="text-lg md:text-sm text-white" />
                </div>
                <div className="min-w-0 flex-1 flex flex-col">
                  <p className="text-slate-500 text-sm md:text-xs tracking-wide mb-1 pl-8" style={{fontSize:"12px"}}>
                    Teléfonos
                  </p>

                  <div className="flex flex-col gap-1.5">
                    <div className="flex flex-row items-baseline gap-1">
                      <span className="text-slate-600 text-base md:text-sm font-bold">
                        Guayaquil:
                      </span>
                      <span className="text-title font-semibold text-base md:text-sm">
                        {footerProps.telf1}
                      </span>
                    </div>
                    <div className="flex flex-row items-baseline gap-1">
                      <span className="text-slate-600 text-base md:text-sm font-bold">
                        Cuenca:
                      </span>
                      <span className="text-title font-semibold text-base md:text-sm">
                        {footerProps.telf2}
                      </span>
                    </div>
                  </div>
                </div>
              </div>





              {footerProps.emailParts && (
                <div
                  className="flex items-center space-x-3 group cursor-pointer"
                  onClick={handleCopyEmail}
                  title="Copiar email"
                >
                  <div
                    className="w-14 md:w-10 h-14 md:h-10 bg-gradient-to-br from-primary to-red-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 flex-shrink-0"
                  >
                    <FontAwesomeIcon icon={faEnvelope} className="text-lg md:text-sm text-white" />
                  </div>
                  <div className="min-w-0 flex-1 select-none">
                    <p className="text-slate-600 text-lg md:text-xs">
                      Email
                    </p>
                    <div className="flex items-center gap-2">
                      <p
                        className="text-title font-semibold text-lg md:text-xs leading-tight"
                        aria-label="Correo electrónico"
                      >
                        <span>{footerProps.emailDisplay}</span>
                        <span aria-hidden="true">@</span>
                        <span>{footerProps.emailDomain}</span>
                      </p>
                      {copied ? (
                        <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                      ) : (
                        <Copy className="w-4 h-4 text-slate-600 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Products/Services Section */}
          {footerProps.products?.items?.length > 0 && (
            <div className={`space-y-4 transition-all duration-400 ease-out ${gridInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`} style={{ transitionDelay: "160ms" }}>
              <h3
                className="text-2xl md:text-xl font-bold text-title mb-4"
              >
                {footerProps.products.title}
              </h3>
              <ul className="space-y-3 md:space-y-2">
                {footerProps.products.items.map((item, index) => (
                  <li key={index}>
                    <a
                      href={item.link}
                      className="text-slate-600 hover:text-primary transition-all duration-200 hover:translate-x-1 font-medium text-xl md:text-sm"
                    >
                      {item.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Social Media & Legal Section */}
          <div className={`space-y-4 transition-all duration-400 ease-out ${gridInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`} style={{ transitionDelay: "240ms" }}>
            {/* Social Media */}
            {footerProps.socialMedia?.links?.length > 0 && (
              <>
                <h3
                  className="text-2xl md:text-xl font-bold text-title mb-4"
                >
                  {footerProps.socialMedia.title}
                </h3>
                <div className="flex flex-wrap gap-4 md:gap-3 mb-6">
                  {footerProps.socialMedia.links.map((social, index) => {
                    const iconClass = socialIcons[social.icon];
                    return (
                      <Link
                        key={index}
                        target="_blank"
                        href={social.url}
                        className="bg-gradient-to-br from-primary to-red-600 hover:shadow-lg p-4 md:p-3 rounded-xl transition-all duration-300 flex items-center justify-center hover:scale-110 active:scale-90"
                        aria-label={social.name}
                      >
                        {iconClass && (
                          <FontAwesomeIcon icon={iconClass} className="text-2xl md:text-xl text-white" />
                        )}
                      </Link>
                    );
                  })}
                </div>
              </>
            )}


          </div>
        </div>

        {/* Bottom Section */}
        <div
          ref={bottomRef}
          className={`border-t border-slate-200 pt-6 transition-all duration-600 ease-out ${bottomInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}
        >
          <div className="flex flex-col md:flex-row justify-between items-center space-y-3 md:space-y-0">
            <p
              className="text-slate-600 text-lg md:text-sm font-medium text-center md:text-left"
            >
              © {new Date().getFullYear()}{" "}
              {footerProps.companyName}. Todos los derechos
              reservados.
            </p>

          </div>
        </div>
      </div>
    </footer>
  );
}
